import * as Yup from 'yup';
import { FormikProvider, getIn, useFormik } from 'formik';
import ButtonWithLoader from '../../../../common/components/ui/loading/ButtonLoading';
import InputFormik from '../../../../common/components/form/InputFormik';
import { maskCpfOrCnpj, maskPhone } from '../../../../common/helpers/mask.helpers';
import { useToastContext } from '../../../../common/components/ui/toast/toast.context';
import { IIndicators, emptyIndicator } from '../../../../types/Indicators.type';
import { updateIndicator } from '../../services/indicator.api';
import { useContext, useEffect, useMemo, useState } from 'react';
import DataBankFields from './fields/DataBankFields';
import TextareaFormik from '../../../renters/components/forms/TextareaFormik';
import { AuthContext } from '../../../../common/components/auth/authContext/AuthContext';
import { ImCheckboxChecked, ImCheckboxUnchecked } from 'react-icons/im';
import NetrinActivate from '../../../leads/components/forms/fields/NetrinActivate';
import { isCPF } from '../../../../common/helpers/is-cpf';

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Nome completo é obrigatório'),
  whatsapp: Yup.string().required('Número WhatsApp obrigatório'),
  cpf: Yup.string().required('CPF é obrigatório'),
  dataBank: Yup.object().shape({
    typeTransaction: Yup.string().required('Tipo de transação é obrigatório'),
    tipoDeChave: Yup.string().required('Tipo de chave é obrigatório'),
    chavePix: Yup.string().required('Chave pix é obrigatória'),
  }),
});

type FormPropsCreateIndicator = {
  onSubmit?: (values) => unknown;
  onSave: (indicatorId: string) => void
  data?: IIndicators
  formRequestErrors?: { message: string, path: string[] }[]
};

export default function FormEditIndicator(props: FormPropsCreateIndicator) {
  const authCtx = useContext(AuthContext);
  const userEmail = authCtx.auth?.user?.email;

  const { notifySuccess, notifyError } = useToastContext()
  const [validCPF, setValidCPF] = useState(false)

  const formik = useFormik({
    initialValues: Object.assign(emptyIndicator(), props.data as IIndicators, {
      birthDate: (props.data?.birthDate || '').split('T')[0],
      observations: props.data?.observations || [{ text: '', date: new Date().toISOString(), author: '' }],
    }),
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (data) => {

      if (isActiveRentalPoint && !formik.values.nameRentalPoint) {
        notifyError("É obrigatório a descrição do Ponto de Locação!");
        return;
      }

      data.observations.forEach(observation => {
        if (!observation.date) {
          observation.date = new Date().toISOString();
        }

        if (!observation.author) {  // Only set the author if it hasn't been set yet
          observation.author = userEmail || "";
        }
      });

      const dataToSave = Object.assign({}, data) as IIndicators
      const result = await updateIndicator(dataToSave._id!, dataToSave)
      if (!result.ok) {
        notifyError(result.message as string)
        return
      }
      notifySuccess("Atualizado com sucesso")
      props.onSave && props.onSave(data._id!)
    }
  })

  useEffect(() => {
    const validCPF = isCPF(formik.values.cpf);
    setValidCPF(validCPF);
  }, [formik.values.cpf]);

  const observations = getIn(formik.values, 'observations')
  const currentObservation = useMemo(() => {
    const total = observations.filter(({ date }) => !!date)
    return total.length
  }, [observations])

  const age = useMemo(() => {
    if (formik.values.birthDate) {
      const birthDate = new Date(formik.values.birthDate);
      const today = new Date();

      let calculatedAge = today.getFullYear() - birthDate.getFullYear();

      if (today.getMonth() < birthDate.getMonth() ||
        (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate())) {
        calculatedAge--;
      }

      return calculatedAge;
    }

    return null;
  }, [formik.values.birthDate]);

  const [, setCurrentDate] = useState(new Date().toISOString().split('T')[0]);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDate(new Date().toISOString().split('T')[0]);
    }, 60000); // Atualiza a cada minuto

    return () => clearInterval(interval); // Limpa o intervalo quando o componente é desmontado
  }, []);
  const [isActiveRentalPoint, setisActiveRentalPoint] = useState(props.data?.activeRentalPoint);

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormikProvider value={formik}>
        <section className="mb-3">
          <h2 className="text-2xl font-semibold mb-4">Informações dos Indicadores</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

            <InputFormik
              label="Nome completo/Razão Social:"
              name='name'
              required={true}
            />

            <InputFormik
              label="Digite seu CPF/CNPJ:"
              name='cpf'
              mask={maskCpfOrCnpj}
              required={true}
            />

            <InputFormik
              label="WhatsApp:"
              name='whatsapp'
              mask={maskPhone}
              required={true}
            />

            <InputFormik
              label="E-mail:"
              name='email'
              type='email'
            />

            <InputFormik
              label={`Data de nascimento (${age} anos):`}
              name='birthDate'
              type="date"
            />
            <div className=" ml-5 relative color flex items-center">
              <label htmlFor={`activeRentalPoint`} className="block text-sm font-medium text-gray-600">
                {isActiveRentalPoint ? (
                  <div className="flex gap-2 items-center justify-start text-greenPrimary">
                    <ImCheckboxChecked />
                    <span>Ponto de Locação ?</span>
                  </div>
                ) : (
                  <div className="flex gap-2 items-center justify-start">
                    <ImCheckboxUnchecked />
                    <span>Ponto de Locação ?</span>
                  </div>
                )}
                <input
                  id="activeRentalPoint"
                  name="activeRentalPoint"
                  type="checkbox"
                  checked={isActiveRentalPoint}
                  onChange={() => {
                    const newValue = !isActiveRentalPoint;
                    setisActiveRentalPoint(newValue);
                    formik.setFieldValue('activeRentalPoint', newValue);
                  }}
                  className="border p-2 rounded w-full absolute opacity-0"
                />
              </label>
            </div>
          </div>
        </section>
        {isActiveRentalPoint && (
          <div className="relative color flex items-center">
            <section>
              <NetrinActivate
                entityId={props.data?._id ?? ""}
                entityName="Indicator"
                validCPF={validCPF}
                cpf={formik.values.cpf}
                status={formik.values.jurValidation.status}
                validated={formik.values.jurValidation.validated}
              />
            </section>
          </div>
        )}
        <section>
          {isActiveRentalPoint && <InputFormik
            label='Digite a descrição do Ponto de Locação:'
            name='nameRentalPoint'
            required={true}
            className='mt-2 mb-2'
          />}
          <DataBankFields />
        </section>

        <section>
          <div className="w-full dark:text-black bg-gray/40 rounded my-4 p-3">
            <h2 className="font-semibold text-2xl mb-4">Observações</h2>
            <TextareaFormik
              label="Observações"
              name={`observations.${currentObservation}.text`}
              className="w-[90%] min-h-64 rounded shadow-md focus:border-white"
            />
          </div>
        </section>

        {/* Botão de Envio */}
        <div className="flex justify-end">
          <ButtonWithLoader isLoading={formik.isSubmitting} onClick={formik.handleSubmit}> Enviar </ButtonWithLoader>
        </div>
      </FormikProvider>
    </form>
  );
}


